import React, { useMemo } from 'react';
import { humanize, titleize } from 'underscore.string';
import i18next from 'i18next';
import { orderIDRenderer } from '../formatters';
import Badge from 'components/v2/Badge';
import { currencyFormatter, dateFormatter, dateServerSideFilterParams } from '../columns';
import { getBillingStatusBadgeColor, getOverrideBillingStatusBadgeColor, getOrderStatusBadgeColor } from 'helpers/status-bar';
export const orderColumns = [
    {
        headerName: 'Order ID',
        field: 'order_id',
        width: 150,
        cellRenderer: orderIDRenderer,
        filter: 'textFilterWithExclude',
        pinned: 'left',
        checkboxSelection: true,
        headerCheckboxSelection: true,
    },
    {
        headerName: 'Origin',
        field: 'origin',
        width: 150,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Destination',
        field: 'destination',
        width: 150,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Margin',
        field: 'margin',
        width: 120,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Vessel',
        field: 'vessel',
        width: 160,
        filter: 'textFilterWithExclude',
        cellClass: '!whitespace-normal',
        autoHeight: true,
    },
    {
        headerName: 'Accounting Review Date',
        field: 'accounting_review_date',
        width: 180,
        filter: 'agDateColumnFilter',
        filterParams: dateServerSideFilterParams,
        valueFormatter: dateFormatter,
        wrapHeaderText: true,
    },
    {
        headerName: 'Automated Billing Evaluated Paused Timestamp',
        field: 'automated_billing_evaluated_paused_timestamp',
        width: 280,
        filter: 'agDateColumnFilter',
        filterParams: dateServerSideFilterParams,
        valueFormatter: dateFormatter,
        wrapHeaderText: true,
    },
    {
        headerName: 'Automated Billing Evaluation Paused',
        field: 'automated_billing_evaluation_paused',
        width: 250,
        filter: 'agSetColumnFilter',
        wrapHeaderText: true,
    },
    {
        headerName: 'Automated Billing Evaluation Paused Reason',
        field: 'automated_billing_evaluation_paused_reason',
        width: 280,
        filter: 'textFilterWithExclude',
        wrapHeaderText: true,
    },
    {
        headerName: 'Base Rate',
        field: 'base_rate',
        width: 120,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'BCO',
        field: 'bco',
        width: 120,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Billing Status',
        groupId: 'billingStatus',
        children: [
            {
                headerName: 'Billable Event',
                field: 'billing_status_billable_event',
                width: 150,
                filter: 'agSetColumnFilter',
                cellRenderer: ({ value }) => {
                    return value ? (React.createElement(Badge, { color: "green" }, titleize(humanize(i18next.t(value))))) : null;
                },
            },
            {
                headerName: 'Billing Status',
                field: 'billing_status_billing_status',
                width: 150,
                filter: 'agSetColumnFilter',
                cellRenderer: ({ value }) => {
                    return value ? (React.createElement(Badge, { color: getBillingStatusBadgeColor(value) }, titleize(humanize(i18next.t(value))))) : null;
                },
            },
            {
                headerName: 'Billing Status Updated At',
                field: 'billing_status_billing_status_updated_at',
                width: 180,
                filter: 'agDateColumnFilter',
                filterParams: dateServerSideFilterParams,
                valueFormatter: dateFormatter,
                wrapHeaderText: true,
            },
            {
                headerName: 'Invoice Grouping',
                field: 'billing_status_invoice_grouping',
                width: 150,
                filter: 'textFilterWithExclude',
            },
            {
                headerName: 'Override Billing Status Note',
                field: 'billing_status_override_billing_status_note',
                width: 200,
                filter: 'textFilterWithExclude',
                wrapHeaderText: true,
                cellClass: '!whitespace-normal',
            },
            {
                headerName: 'Override Billing Status Reason Codes',
                field: 'billing_status_override_billing_status_reason_codes',
                width: 230,
                filter: 'textFilterWithExclude',
                wrapHeaderText: true,
            },
            {
                headerName: 'Pending Accessorial Evaluation',
                field: 'billing_status_pending_accessorial_evaluation',
                width: 200,
                filter: 'textFilterWithExclude',
                wrapHeaderText: true,
            },
            {
                headerName: 'Override Billing Status',
                field: 'billing_status_override_billing_status',
                width: 180,
                filter: 'textFilterWithExclude',
                cellRenderer: ({ value }) => {
                    return value ? (React.createElement(Badge, { color: getOverrideBillingStatusBadgeColor(value) }, titleize(humanize(i18next.t(value))))) : null;
                },
            },
            {
                headerName: 'State/Missing',
                groupId: 'billingStatusStateMissing',
                children: [
                    {
                        headerName: 'Unbilled Charges',
                        field: 'billing_status_state_unbilled_charges',
                        width: 150,
                        filter: 'textFilterWithExclude',
                    },
                    {
                        headerName: 'Required Docs',
                        field: 'billing_status_state_required_docs',
                        width: 130,
                        filter: 'textFilterWithExclude',
                    },
                    {
                        headerName: 'Missing Docs',
                        field: 'billing_status_state_missing_docs',
                        width: 130,
                        filter: 'textFilterWithExclude',
                    },
                    {
                        headerName: 'Required Reference Numbers',
                        field: 'billing_status_state_required_reference_numbers',
                        width: 200,
                        filter: 'textFilterWithExclude',
                    },
                    {
                        headerName: 'Missing Reference Numbers',
                        field: 'billing_status_state_missing_reference_numbers',
                        width: 200,
                        filter: 'textFilterWithExclude',
                    },
                    {
                        headerName: 'Missing Accessorials',
                        field: 'billing_status_state_missing_accessorials',
                        width: 160,
                        filter: 'textFilterWithExclude',
                    },
                    {
                        headerName: 'Pending Accessorials',
                        field: 'billing_status_state_pending_accessorials',
                        width: 160,
                        filter: 'textFilterWithExclude',
                    },
                ],
            },
        ],
    },
    {
        headerName: 'Cargo Description',
        field: 'cargo_description',
        width: 180,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Carrier Accessorial Total',
        field: 'carrier_accessorial_total',
        width: 180,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
        wrapHeaderText: true,
    },
    {
        headerName: 'Carrier Cost Total',
        field: 'carrier_cost_total',
        width: 160,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Carrier Fsc Total',
        field: 'carrier_fsc_total',
        width: 150,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Carrier Linehaul',
        field: 'carrier_linehaul',
        width: 150,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Carrier Other Accessorial Total',
        field: 'carrier_other_accessorial_total',
        width: 220,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Carrier Total',
        field: 'carrier_total',
        width: 130,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Container Number',
        field: 'container_number',
        width: 160,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Created At',
        field: 'created_at',
        width: 160,
        filter: 'agDateColumnFilter',
        filterParams: dateServerSideFilterParams,
        valueFormatter: dateFormatter,
    },
    {
        headerName: 'Created By',
        field: 'created_by',
        width: 150,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Date Created',
        field: 'date_created',
        width: 150,
        filter: 'agDateColumnFilter',
        filterParams: dateServerSideFilterParams,
        valueFormatter: dateFormatter,
    },
    {
        headerName: 'Destination City',
        field: 'destination_city',
        width: 150,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Destination Name',
        field: 'destination_name',
        width: 180,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Destination State',
        field: 'destination_state',
        width: 150,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Discussion',
        field: 'discussion',
        width: 200,
        filter: 'textFilterWithExclude',
        cellClass: '!whitespace-normal',
    },
    {
        headerName: 'Hazmat Class',
        field: 'hazmat_class',
        width: 130,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Hazmat Description',
        field: 'hazmat_description',
        width: 180,
        filter: 'textFilterWithExclude',
        cellClass: '!whitespace-normal',
    },
    {
        headerName: 'Hazmat Packaging',
        field: 'hazmat_packaging',
        width: 160,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Invoice Cliff Date',
        field: 'invoice_cliff_date',
        width: 160,
        filter: 'agDateColumnFilter',
        filterParams: dateServerSideFilterParams,
        valueFormatter: dateFormatter,
    },
    {
        headerName: 'Market',
        field: 'market',
        width: 120,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Missing Docs Report',
        field: 'missing_docs_report',
        width: 180,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Mode',
        field: 'mode',
        width: 100,
        filter: 'agSetColumnFilter',
    },
    {
        headerName: 'Move Type',
        field: 'move_type',
        width: 120,
        filter: 'agSetColumnFilter',
    },
    {
        headerName: 'Order Carrier',
        field: 'order_carrier',
        width: 150,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Order Received Date',
        field: 'order_received_date',
        width: 180,
        filter: 'agDateColumnFilter',
        filterParams: dateServerSideFilterParams,
        valueFormatter: dateFormatter,
    },
    {
        headerName: 'Order Status',
        field: 'order_status',
        width: 130,
        filter: 'agSetColumnFilter',
        cellRenderer: ({ value }) => {
            return value ? (React.createElement(Badge, { color: getOrderStatusBadgeColor(value) }, titleize(humanize(i18next.t(value))))) : null;
        },
    },
    {
        headerName: 'Order Timezone',
        field: 'order_timezone',
        width: 150,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Order Type',
        field: 'order_type',
        width: 120,
        filter: 'agSetColumnFilter',
    },
    {
        headerName: 'Order Updated',
        field: 'order_updated',
        width: 160,
        filter: 'agDateColumnFilter',
        filterParams: dateServerSideFilterParams,
        valueFormatter: dateFormatter,
    },
    {
        headerName: 'Origin City',
        field: 'origin_city',
        width: 130,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Origin Name',
        field: 'origin_name',
        width: 160,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Origin State',
        field: 'origin_state',
        width: 130,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Pending Carrier Accessorials',
        field: 'pending_carrier_accessorials',
        width: 200,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Pending Shipper Accessorials',
        field: 'pending_shipper_accessorials',
        width: 200,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Railroads',
        field: 'railroads',
        width: 150,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Reference Numbers',
        field: 'reference_numbers',
        width: 180,
        filter: 'textFilterWithExclude',
        filterValueGetter: (params) => {
            var _a;
            const refs = (_a = params.data) === null || _a === void 0 ? void 0 : _a.reference_numbers;
            if (!refs || !Array.isArray(refs))
                return '';
            return refs.map((ref) => `${ref.name} ${ref.value}`).join(' ');
        },
        autoHeight: true,
        cellRenderer: ({ value }) => {
            if (!value || !Array.isArray(value) || value.length === 0) {
                return null;
            }
            return (React.createElement("div", null, value.map((ref, index) => (React.createElement("div", { key: ref._id || index, className: index < value.length - 1 ? 'mb-2' : '' },
                React.createElement("div", { className: "font-bold" }, titleize(humanize(ref.name))),
                React.createElement("div", null, ref.value))))));
        },
    },
    {
        headerName: 'Service Type',
        field: 'service_type',
        width: 130,
        filter: 'agSetColumnFilter',
    },
    {
        headerName: 'Shipment ID',
        field: 'shipment_id',
        width: 140,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Shipper',
        field: 'shipper',
        width: 180,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Shipper Accessorial Total',
        field: 'shipper_accessorial_total',
        width: 180,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Shipper Account Support Agent',
        field: 'shipper_account_support_agent',
        width: 220,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Shipper Account Support Agent Tier2',
        field: 'shipper_account_support_agent_tier2',
        width: 250,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Shipper Billing Rep',
        field: 'shipper_billing_rep',
        width: 170,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Shipper Chassis Total',
        field: 'shipper_chassis_total',
        width: 170,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Shipper Cost Total',
        field: 'shipper_cost_total',
        width: 160,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Shipper Customer Success Manager',
        field: 'shipper_customer_success_manager',
        width: 240,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Shipper Data Entry Agent',
        field: 'shipper_data_entry_agent',
        width: 190,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Shipper Demurrage Total',
        field: 'shipper_demurrage_total',
        width: 190,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Shipper FSC Total',
        field: 'shipper_fsc_total',
        width: 150,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Shipper Linehaul',
        field: 'shipper_linehaul',
        width: 150,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
    },
    {
        headerName: 'Shipper Ops Group',
        field: 'shipper_ops_group',
        width: 160,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Shipper Ops Manager',
        field: 'shipper_ops_manager',
        width: 170,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Shipper Other Accessorial Total',
        field: 'shipper_other_accessorial_total',
        width: 220,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Shipper Sales Rep',
        field: 'shipper_sales_rep',
        width: 160,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Shipper Storage Total',
        field: 'shipper_storage_total',
        width: 170,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Shipper Total',
        field: 'shipper_total',
        width: 130,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Shorthaul Ops Customer Success',
        field: 'shorthaul_ops_customer_success',
        width: 230,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Shorthaul Ops Operations Manager',
        field: 'shorthaul_ops_operations_manager',
        width: 240,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Standard Reference Numbers',
        groupId: 'standardRefNumbers',
        children: [
            {
                headerName: 'Booking Number',
                field: 'standard_reference_numbers_booking_number',
                width: 150,
                filter: 'textFilterWithExclude',
            },
            {
                headerName: 'Category',
                field: 'standard_reference_numbers_category',
                width: 120,
                filter: 'textFilterWithExclude',
            },
            {
                headerName: 'Delivery Order',
                field: 'standard_reference_numbers_delivery_order',
                width: 150,
                filter: 'textFilterWithExclude',
            },
            {
                headerName: 'Final Destination',
                field: 'standard_reference_numbers_final_destination',
                width: 150,
                filter: 'textFilterWithExclude',
            },
            {
                headerName: 'Master BOL',
                field: 'standard_reference_numbers_master_bol',
                width: 130,
                filter: 'textFilterWithExclude',
            },
            {
                headerName: 'MO Number',
                field: 'standard_reference_numbers_mo_number',
                width: 130,
                filter: 'textFilterWithExclude',
            },
            {
                headerName: 'Mode',
                field: 'standard_reference_numbers_mode',
                width: 100,
                filter: 'textFilterWithExclude',
            },
            {
                headerName: 'PO Number',
                field: 'standard_reference_numbers_po_number',
                width: 130,
                filter: 'textFilterWithExclude',
            },
            {
                headerName: 'Work Order Number',
                field: 'standard_reference_numbers_work_order_number',
                width: 160,
                filter: 'textFilterWithExclude',
            },
        ],
    },
    {
        headerName: 'Steamship',
        field: 'steamship',
        width: 150,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Team Commercial Account Manager',
        field: 'team_commercial_account_manager',
        width: 240,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Team Commercial Sales Rep',
        field: 'team_commercial_sales_rep',
        width: 210,
        filter: 'textFilterWithExclude',
    },
    // next two under common header?
    {
        headerName: 'Team Drayage Ops Customer Success',
        field: 'team_drayage_ops_customer_success',
        width: 260,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Team Drayage Ops Data Entry',
        field: 'team_drayage_ops_data_entry',
        width: 220,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Total',
        field: 'total',
        width: 120,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Total Invoiced',
        field: 'total_invoiced',
        width: 140,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Train',
        field: 'train',
        width: 120,
        filter: 'textFilterWithExclude',
    },
    {
        headerName: 'Unbilled Approved',
        field: 'unbilled_approved',
        width: 160,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
    },
    {
        headerName: 'Unbilled Charges',
        field: 'unbilled_charges',
        width: 150,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
    },
    {
        headerName: 'Unbilled Pending',
        field: 'unbilled_pending',
        width: 150,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
    },
    {
        headerName: 'Vendor Total',
        field: 'vendor_total',
        width: 130,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
    },
    {
        headerName: 'Weight',
        field: 'weight',
        width: 120,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
    },
];
export const useOrderColumns = () => {
    return useMemo(() => orderColumns, []);
};
